/**
 * TT3 bytecode framing (docs/formats/ttm-animation.md, VERIFIED against the
 * full 28-file Red Baron `.TTM` corpus — 0 slack on every file, both ways):
 *
 * ```
 * uint16le code
 * op    = code & 0xFFF0
 * count = code & 0x000F
 *
 * if count <= 8:
 *     int16le args[count]
 * elif count == 0x0F:
 *     if op is DRAW_EMPTY_POLY_OP or DRAW_FILLED_POLY_OP:
 *         int16le nPoints; int16le x[nPoints]; int16le y[nPoints]
 *     else:
 *         inline string — see readInlineString below
 * else:
 *     structural violation: 9..14 is not a valid arg-count nibble
 * ```
 *
 * The point-list special case is the thing the original spec draft got
 * wrong (a naive "count==0x0F always means string" walker desyncs on any
 * script using `DRAW EMPTY/FILLED POLY` and overruns the buffer near the
 * end) — see docs/formats/ttm-animation.md's tail-byte account. With it
 * handled, every one of the 28 real Red Baron `.TTM` scripts (23 catalog +
 * 5 loose expansion-only) disassembles to the exact byte, and re-encoding
 * the resulting instruction list reproduces the original bytecode
 * byte-for-byte — the strongest evidence this framing is fully understood.
 */

import { BinaryReader } from "./binary-reader.ts";
import { DRAW_EMPTY_POLY_OP, DRAW_FILLED_POLY_OP, ttmOpcodeName } from "./ttm-opcodes.ts";

/** Largest arg-count nibble that means "this many `int16` args follow"; `0x0F` is the string/point-list sentinel. */
const MAX_INT_ARGS = 8;
const STRING_OR_POINTS_NIBBLE = 0x0f;

export interface TtmPoint {
  readonly x: number;
  readonly y: number;
}

/** An instruction's decoded payload, discriminated by its wire encoding. */
export type TtmArgs =
  | { readonly form: "ints"; readonly values: readonly number[] }
  | { readonly form: "string"; readonly value: string }
  | { readonly form: "points"; readonly points: readonly TtmPoint[] };

export interface TtmInstruction {
  /** Byte offset of this instruction's opcode word within the (decompressed) TT3 bytecode. */
  readonly offset: number;
  /** The full 16-bit code as stored: `op | count`. */
  readonly opcode: number;
  /** `opcode & 0xFFF0` — the base opcode used for name lookup; for a few families (DRAW STRING n, ...) this already bakes in a sub-index. */
  readonly op: number;
  /** `opcode & 0x000F` — the arg-count nibble (0-8), or `0x0F` for the string/point-list forms. */
  readonly count: number;
  /** Mnemonic from the opcode table (ttm-opcodes.ts), or `undefined` if `op` isn't in it. */
  readonly name: string | undefined;
  /** `"unknown"` when `name` is `undefined` — the payload is still parsed structurally either way, never guessed from the name. */
  readonly kind: "known" | "unknown";
  readonly args: TtmArgs;
  /** Total encoded length of this instruction (opcode word + payload), in bytes. */
  readonly length: number;
}

/**
 * Disassembles a whole (already-decompressed) TT3 bytecode buffer into its
 * instruction list. Never throws on an opcode whose name isn't in the
 * table (`kind` becomes `"unknown"`, payload still parsed per the framing
 * rule above) — only a structural violation throws: an invalid arg-count
 * nibble (9-14), or a read past the end of `bytes` (truncated/corrupt
 * buffer, via `BinaryReader`'s bounds checks).
 */
export function disassembleTtmBytecode(bytes: ArrayBufferLike | ArrayBufferView): TtmInstruction[] {
  const reader = new BinaryReader(bytes);
  const instructions: TtmInstruction[] = [];
  while (reader.remaining() > 0) {
    instructions.push(readInstruction(reader));
  }
  return instructions;
}

function readInstruction(reader: BinaryReader): TtmInstruction {
  const offset = reader.tell();
  const opcode = reader.u16();
  const op = opcode & 0xfff0;
  const count = opcode & 0x000f;

  if (count > MAX_INT_ARGS && count !== STRING_OR_POINTS_NIBBLE) {
    throw new Error(
      `ttm: opcode 0x${opcode.toString(16).padStart(4, "0")} at offset ${offset} has an invalid arg-count ` +
        `nibble ${count} (0x${count.toString(16)}) — only 0-${MAX_INT_ARGS} or 0xF (string/point-list) are valid`,
    );
  }

  const args = readArgs(reader, op, count);
  const name = ttmOpcodeName(op);
  return { offset, opcode, op, count, name, kind: name === undefined ? "unknown" : "known", args, length: reader.tell() - offset };
}

function readArgs(reader: BinaryReader, op: number, count: number): TtmArgs {
  if (count !== STRING_OR_POINTS_NIBBLE) {
    const values: number[] = [];
    for (let i = 0; i < count; i++) values.push(reader.i16());
    return { form: "ints", values };
  }
  if (op === DRAW_EMPTY_POLY_OP || op === DRAW_FILLED_POLY_OP) {
    return { form: "points", points: readPoints(reader) };
  }
  return { form: "string", value: readInlineString(reader) };
}

function readPoints(reader: BinaryReader): TtmPoint[] {
  const count = reader.i16();
  if (count < 0) {
    throw new Error(`ttm: polygon point count ${count} is negative at offset ${reader.tell() - 2}`);
  }
  const xs: number[] = [];
  for (let i = 0; i < count; i++) xs.push(reader.i16());
  const ys: number[] = [];
  for (let i = 0; i < count; i++) ys.push(reader.i16());
  return xs.map((x, i) => ({ x, y: ys[i]! }));
}

/**
 * Reads the framing's inline-string form: a NUL-terminated string read two
 * bytes (one little-endian word) at a time, so the total advance is always
 * even. Each pair contributes its low byte to the string if nonzero, then
 * its high byte if nonzero; the first pair containing a zero byte ends the
 * string (that pair is still fully consumed). Bytes decode one-to-one into
 * UTF-16 code units, matching `BinaryReader.cstring`'s CP437-agnostic
 * convention — every string this format carries is a DOS filename, plain
 * ASCII in the whole corpus.
 */
function readInlineString(reader: BinaryReader): string {
  let value = "";
  for (;;) {
    const lo = reader.u8();
    const hi = reader.u8();
    if (lo !== 0) value += String.fromCharCode(lo);
    if (hi !== 0) value += String.fromCharCode(hi);
    if (lo === 0 || hi === 0) break;
  }
  return value;
}

/**
 * Re-encodes a disassembled instruction list back into TT3 bytecode bytes.
 * This does not replay any raw bytes captured during disassembly — it
 * rebuilds the wire format purely from each instruction's `opcode` and
 * `args`, which is what makes an exact match against the original bytecode
 * (proven for the whole 28-file corpus, see ttm.test.ts) real evidence the
 * encoding is understood, not a trivial identity check.
 */
export function encodeTtmBytecode(instructions: readonly TtmInstruction[]): Uint8Array {
  const parts = instructions.map(encodeInstruction);
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const part of parts) {
    out.set(part, pos);
    pos += part.length;
  }
  return out;
}

function encodeInstruction(instruction: TtmInstruction): Uint8Array {
  const payload = encodeArgs(instruction.args);
  const out = new Uint8Array(2 + payload.length);
  new DataView(out.buffer).setUint16(0, instruction.opcode, true);
  out.set(payload, 2);
  return out;
}

function encodeArgs(args: TtmArgs): Uint8Array {
  switch (args.form) {
    case "ints":
      return encodeInts(args.values);
    case "string":
      return encodeInlineString(args.value);
    case "points":
      return encodePoints(args.points);
  }
}

function encodeInts(values: readonly number[]): Uint8Array {
  const out = new Uint8Array(values.length * 2);
  const view = new DataView(out.buffer);
  values.forEach((value, i) => view.setInt16(i * 2, value, true));
  return out;
}

/**
 * Inverse of `readInlineString`: content bytes, then a NUL terminator,
 * padded with one more zero byte when needed so the total is even. This is
 * provably the original encoding, not just *an* encoding that redecodes to
 * the same string: the pad byte must be exactly zero for `readInlineString`
 * to have terminated there at all, and the minimal-padding total is the
 * only length consistent with that termination point.
 */
function encodeInlineString(value: string): Uint8Array {
  const contentLength = value.length;
  const totalLength = contentLength % 2 === 0 ? contentLength + 2 : contentLength + 1;
  const out = new Uint8Array(totalLength); // zero-initialized: NUL terminator (+ optional pad byte) for free
  for (let i = 0; i < contentLength; i++) out[i] = value.charCodeAt(i);
  return out;
}

function encodePoints(points: readonly TtmPoint[]): Uint8Array {
  const out = new Uint8Array(2 + points.length * 4);
  const view = new DataView(out.buffer);
  view.setInt16(0, points.length, true);
  let offset = 2;
  for (const point of points) {
    view.setInt16(offset, point.x, true);
    offset += 2;
  }
  for (const point of points) {
    view.setInt16(offset, point.y, true);
    offset += 2;
  }
  return out;
}
